import React, { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Users, FileText, Clock, CheckCircle2, MessageSquare, Newspaper, Plus, Trash2, Image, Upload, Sparkles } from "lucide-react";

const STATUSES = ["Received", "In Progress", "Approved", "Ready for Pickup", "Completed", "Rejected"];
const NEWS_CATEGORIES = ["Announcement", "Update", "MeeSeva", "Alert"];

const AdminDashboard = () => {
    const { t } = useApp();
    const { user } = useAuth();
    const [tab, setTab] = useState("bookings");
    const [stats, setStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [news, setNews] = useState([]);
    const [newsForm, setNewsForm] = useState({ title: "", summary: "", body: "", category: "Announcement", image_url: "" });
    const [creating, setCreating] = useState(false);
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [gallery, setGallery] = useState([]);
    const [galleryForm, setGalleryForm] = useState({ caption: "", category: "Center" });
    const [galleryUploading, setGalleryUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        try {
            const [s, b, c, n, g] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/bookings"),
                api.get("/contact"),
                api.get("/news/all"),
                api.get("/gallery"),
            ]);
            setStats(s.data); setBookings(b.data); setContacts(c.data); setNews(n.data); setGallery(g.data);
        } finally { setLoading(false); }
    };

    useEffect(() => { loadAll(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/bookings/${id}/status`, { status, admin_note: "" });
            toast.success("Status updated");
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const createNews = async (e) => {
        e.preventDefault();
        if (!newsForm.title || !newsForm.body) { toast.error("Title and body are required"); return; }
        setCreating(true);
        try {
            await api.post("/news", { ...newsForm, is_published: true });
            toast.success("News published");
            setNewsForm({ title: "", summary: "", body: "", category: "Announcement", image_url: "" });
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setCreating(false); }
    };

    const deleteNews = async (id) => {
        if (!window.confirm("Delete this news item?")) return;
        try {
            await api.delete(`/news/${id}`);
            toast.success("Deleted");
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const uploadGallery = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setGalleryUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post("/gallery", { url: data.url, caption: galleryForm.caption, category: galleryForm.category });
            setGalleryForm({ caption: "", category: "Center" });
            e.target.value = "";
            toast.success("Photo added to gallery");
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setGalleryUploading(false);
        }
    };

    const deleteGallery = async (id) => {
        if (!window.confirm("Remove this photo?")) return;
        try {
            await api.delete(`/gallery/${id}`);
            toast.success("Removed");
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const generateNews = async () => {
        if (!aiPrompt.trim()) { toast.error("Type a short prompt first"); return; }
        setAiLoading(true);
        try {
            const { data } = await api.post("/news/generate", { prompt: aiPrompt });
            setNewsForm({
                title: data.title || "",
                summary: data.summary || "",
                body: data.body || "",
                category: data.category || "Announcement",
                image_url: newsForm.image_url,
            });
            toast.success("Draft ready — review & publish");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <div data-testid="page-admin-dashboard" className="container-x section-pad">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <div className="chip bg-[hsl(var(--gold))]/20 text-[hsl(var(--royal-deep))] dark:text-[hsl(var(--gold-light))] mb-2">Admin</div>
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{t.admin.title}</h1>
                    <p className="text-muted-foreground mt-1">{user?.email}</p>
                </div>
            </div>

            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
                    <Stat icon={FileText} color="royal" label={t.admin.total} value={stats.total_bookings} />
                    <Stat icon={Clock} color="amber" label={t.admin.pending} value={stats.pending} />
                    <Stat icon={CheckCircle2} color="emerald" label={t.admin.completed} value={stats.completed} />
                    <Stat icon={Users} color="royal" label={t.admin.customers} value={stats.customers} />
                    <Stat icon={MessageSquare} color="gold" label={t.admin.messages} value={stats.contacts} />
                </div>
            )}

            <div className="flex gap-2 border-b border-border mb-6 overflow-x-auto">
                {[["bookings", t.admin.bookings], ["contacts", t.admin.contacts], ["news", t.admin.news], ["gallery", t.admin.gallery]].map(([k, label]) => (
                    <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                        className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === k ? "border-[hsl(var(--royal))] text-[hsl(var(--royal))]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-16 text-center text-muted-foreground">Loading…</div>
            ) : tab === "bookings" ? (
                <div className="overflow-x-auto rounded-2xl border border-border">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                            <tr>
                                <Th>{t.admin.reference}</Th><Th>{t.admin.service}</Th><Th>{t.admin.customer}</Th>
                                <Th>{t.admin.phone}</Th><Th>{t.admin.status}</Th><Th>{t.admin.date}</Th><Th>{t.admin.actions}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookings.map((b) => (
                                <tr key={b.id} data-testid={`admin-row-${b.reference}`} className="border-t border-border hover:bg-muted/30">
                                    <Td className="font-mono font-semibold text-[hsl(var(--royal))]">{b.reference}</Td>
                                    <Td>{b.service_name}</Td>
                                    <Td>{b.full_name}</Td>
                                    <Td>{b.phone}</Td>
                                    <Td>
                                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted">{b.status}</span>
                                    </Td>
                                    <Td className="text-xs">{new Date(b.created_at).toLocaleDateString()}</Td>
                                    <Td>
                                        <select data-testid={`status-select-${b.reference}`} defaultValue={b.status} onChange={(e) => updateStatus(b.id, e.target.value)}
                                            className="rounded-lg border border-border bg-background px-2 py-1 text-xs focus:border-[hsl(var(--royal))] focus:outline-none">
                                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </Td>
                                </tr>
                            ))}
                            {bookings.length === 0 && <tr><Td colSpan={7} className="text-center text-muted-foreground py-8">No bookings yet.</Td></tr>}
                        </tbody>
                    </table>
                </div>
            ) : tab === "contacts" ? (
                <div className="grid gap-4">
                    {contacts.map((c) => (
                        <div key={c.id} className="rounded-2xl border border-border bg-card p-5">
                            <div className="flex flex-wrap justify-between gap-2">
                                <div><div className="font-semibold">{c.name}</div><div className="text-xs text-muted-foreground">{c.email} · {c.phone}</div></div>
                                <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</div>
                            </div>
                            {c.subject && <div className="mt-2 text-sm font-medium">{c.subject}</div>}
                            <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{c.message}</p>
                        </div>
                    ))}
                    {contacts.length === 0 && <div className="py-8 text-center text-muted-foreground">No messages yet.</div>}
                </div>
            ) : tab === "news" ? (
                <div className="grid gap-6 lg:grid-cols-2">
                    <form data-testid="admin-news-form" onSubmit={createNews} className="rounded-3xl border border-border bg-card p-6 space-y-3 h-fit">
                        <h3 className="text-lg font-semibold flex items-center gap-2"><Plus size={18} /> {t.admin.addNews}</h3>
                        <div className="rounded-2xl border border-dashed border-[hsl(var(--royal))]/40 bg-[hsl(var(--royal))]/5 p-3 space-y-2">
                            <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--royal))]">
                                <Sparkles size={14} /> AI news writer (Gemini)
                            </div>
                            <textarea data-testid="ai-prompt" rows={2} value={aiPrompt}
                                onChange={(e) => setAiPrompt(e.target.value)}
                                placeholder="e.g., UIDAI extended free Aadhaar update deadline to March 2026"
                                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:border-[hsl(var(--royal))] focus:outline-none" />
                            <button type="button" data-testid="ai-generate" onClick={generateNews} disabled={aiLoading}
                                className="btn-royal w-full justify-center !py-2 !text-xs disabled:opacity-40">
                                {aiLoading ? "Generating…" : "Draft with AI"}
                            </button>
                        </div>
                        <input data-testid="news-title" required placeholder={t.admin.newTitle} value={newsForm.title}
                            onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                            className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 focus:border-[hsl(var(--royal))] focus:outline-none text-sm" />
                        <select data-testid="news-category" value={newsForm.category}
                            onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                            className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 focus:border-[hsl(var(--royal))] focus:outline-none text-sm">
                            {NEWS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input data-testid="news-image" placeholder={t.admin.newImage} value={newsForm.image_url}
                            onChange={(e) => setNewsForm({ ...newsForm, image_url: e.target.value })}
                            className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 focus:border-[hsl(var(--royal))] focus:outline-none text-sm" />
                        <textarea data-testid="news-summary" placeholder={t.admin.newSummary} rows={2} value={newsForm.summary}
                            onChange={(e) => setNewsForm({ ...newsForm, summary: e.target.value })}
                            className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 focus:border-[hsl(var(--royal))] focus:outline-none text-sm" />
                        <textarea data-testid="news-body" required placeholder={t.admin.newBody} rows={5} value={newsForm.body}
                            onChange={(e) => setNewsForm({ ...newsForm, body: e.target.value })}
                            className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 focus:border-[hsl(var(--royal))] focus:outline-none text-sm" />
                        <button data-testid="news-submit" type="submit" disabled={creating} className="btn-royal w-full justify-center disabled:opacity-40">
                            {creating ? "Publishing…" : t.admin.addNews}
                        </button>
                    </form>

                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold flex items-center gap-2"><Newspaper size={18} /> Published news</h3>
                        {news.length === 0 && <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">No news yet.</div>}
                        {news.map((n) => (
                            <div key={n.id} data-testid={`admin-news-${n.id}`} className="rounded-2xl border border-border bg-card p-4 flex gap-3">
                                {n.image_url && <img src={n.image_url} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />}
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-semibold text-[hsl(var(--royal))]">{n.category} · {new Date(n.published_at).toLocaleDateString()}</div>
                                    <div className="mt-0.5 font-semibold text-sm leading-snug line-clamp-2">{n.title}</div>
                                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{n.summary}</div>
                                </div>
                                <button onClick={() => deleteNews(n.id)} data-testid={`news-delete-${n.id}`}
                                    className="w-9 h-9 rounded-full hover:bg-destructive/10 hover:text-destructive text-muted-foreground flex items-center justify-center shrink-0"
                                    aria-label="Delete">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div data-testid="admin-gallery-panel" className="grid gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-1 rounded-3xl border border-border bg-card p-6 space-y-3 h-fit">
                        <h3 className="text-lg font-semibold flex items-center gap-2"><Image size={18} /> {t.admin.addPhoto}</h3>
                        <select data-testid="gallery-category" value={galleryForm.category}
                            onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                            className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 focus:border-[hsl(var(--royal))] focus:outline-none text-sm">
                            {["Center", "Team", "Services", "Events", "Certificates"].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input data-testid="gallery-caption" placeholder={t.admin.caption} value={galleryForm.caption}
                            onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })}
                            className="w-full rounded-xl border-2 border-border bg-background px-4 py-2.5 focus:border-[hsl(var(--royal))] focus:outline-none text-sm" />
                        <label className={`cursor-pointer flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border hover:border-[hsl(var(--royal))] p-6 text-center text-sm transition-colors ${galleryUploading ? "opacity-50 pointer-events-none" : ""}`}>
                            <Upload size={18} className="text-[hsl(var(--royal))]" />
                            <span>{galleryUploading ? "Uploading…" : t.admin.chooseImage}</span>
                            <input data-testid="gallery-file" type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={uploadGallery} />
                        </label>
                        <p className="text-[11px] text-muted-foreground">Photo uploads instantly to the public Gallery page.</p>
                    </div>
                    <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3">
                        {gallery.map((g) => {
                            const src = g.url && g.url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${g.url}` : g.url;
                            return (
                                <div key={g.id} data-testid={`admin-gallery-${g.id}`} className="relative rounded-2xl overflow-hidden border border-border group aspect-square">
                                    <img src={src} alt={g.caption} className="w-full h-full object-cover" />
                                    <button onClick={() => deleteGallery(g.id)} data-testid={`gallery-delete-${g.id}`}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur text-destructive hover:bg-destructive hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        aria-label="Delete">
                                        <Trash2 size={14} />
                                    </button>
                                    <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] font-medium">
                                        <div className="line-clamp-1">{g.caption || g.category}</div>
                                    </div>
                                </div>
                            );
                        })}
                        {gallery.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No photos yet. Upload one to publish it on the public Gallery page.</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

const COLOR = {
    royal: "bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))]",
    gold: "bg-[hsl(var(--gold))]/20 text-[hsl(var(--royal-deep))] dark:text-[hsl(var(--gold-light))]",
    emerald: "bg-emerald-500/10 text-emerald-600",
    amber: "bg-amber-500/10 text-amber-600",
};

const Stat = ({ icon: Icon, label, value, color = "royal" }) => (
    <div className="rounded-2xl border border-border bg-card p-5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${COLOR[color]}`}><Icon size={18} /></div>
        <div className="mt-4 text-3xl font-bold tracking-tight">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
);
const Th = ({ children }) => <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`px-4 py-3 align-middle ${className}`}>{children}</td>;

export default AdminDashboard;
